/**
 * OKS-50 — Lucide icon registry for badges.
 *
 * `BadgeCatalog::CUSTOM_ICONS` (Ruby) defines the curated allow-list
 * of lucide identifiers brand admins can pick from. This map is the
 * FE counterpart — name → component — so the runtime can render the
 * right glyph without dynamic imports (lucide-react ships individual
 * components per icon).
 *
 * The set MUST stay 1:1 with `BadgeCatalog::CUSTOM_ICONS`. Each new
 * icon added there must also land here; the form / tile fall back to
 * a generic Award glyph when an unknown name is passed (defensive
 * — a stale FE bundle never throws).
 */
import {
  Award,
  Cake,
  CalendarCheck,
  Camera,
  Cherry,
  Clapperboard,
  Cookie,
  Crown,
  Dumbbell,
  Eye,
  Flag,
  Flame,
  Gauge,
  Gem,
  Gift,
  Handshake,
  Heart,
  Leaf,
  type LucideIcon,
  Medal,
  Megaphone,
  Mic,
  Moon,
  Music,
  Rocket,
  Shield,
  Smile,
  Sparkles,
  Star,
  Sun,
  Target,
  ThumbsUp,
  TrendingUp,
  Trophy,
  Zap,
} from "lucide-react"

export const BADGE_ICON_MAP: Record<string, LucideIcon> = {
  trophy: Trophy,
  star: Star,
  flame: Flame,
  rocket: Rocket,
  crown: Crown,
  target: Target,
  award: Award,
  medal: Medal,
  sparkles: Sparkles,
  heart: Heart,
  zap: Zap,
  shield: Shield,
  gem: Gem,
  gift: Gift,
  sun: Sun,
  moon: Moon,
  music: Music,
  camera: Camera,
  clapperboard: Clapperboard,
  mic: Mic,
  megaphone: Megaphone,
  smile: Smile,
  "thumbs-up": ThumbsUp,
  flag: Flag,
  gauge: Gauge,
  cookie: Cookie,
  cake: Cake,
  cherry: Cherry,
  leaf: Leaf,
  dumbbell: Dumbbell,
  handshake: Handshake,
  // OKS-558 — net-new §14 achievement families (must stay 1:1 with
  // `BadgeCatalog::CUSTOM_ICONS`).
  eye: Eye,
  "trending-up": TrendingUp,
  "calendar-check": CalendarCheck,
}

export function badgeIcon(name: string): LucideIcon {
  return BADGE_ICON_MAP[name] ?? Award
}

/**
 * Tailwind classes for the 6-token accent palette. Maps the BE
 * `accent_color` token to the colour pair (background tint + icon
 * foreground) used by `<BadgeTile>` for the icon circle.
 */
export const BADGE_ACCENT_CLASSES: Record<
  string,
  { bg: string; fg: string; ring: string }
> = {
  amber: {
    bg: "bg-amber-100 dark:bg-amber-900/40",
    fg: "text-amber-700 dark:text-amber-300",
    ring: "ring-amber-300/40",
  },
  blue: {
    bg: "bg-blue-100 dark:bg-blue-900/40",
    fg: "text-blue-700 dark:text-blue-300",
    ring: "ring-blue-300/40",
  },
  orange: {
    bg: "bg-orange-100 dark:bg-orange-900/40",
    fg: "text-orange-700 dark:text-orange-300",
    ring: "ring-orange-300/40",
  },
  purple: {
    bg: "bg-purple-100 dark:bg-purple-900/40",
    fg: "text-purple-700 dark:text-purple-300",
    ring: "ring-purple-300/40",
  },
  yellow: {
    bg: "bg-yellow-100 dark:bg-yellow-900/40",
    fg: "text-yellow-700 dark:text-yellow-300",
    ring: "ring-yellow-300/40",
  },
  green: {
    bg: "bg-green-100 dark:bg-green-900/40",
    fg: "text-green-700 dark:text-green-300",
    ring: "ring-green-300/40",
  },
}

export function accentClasses(color: string) {
  return BADGE_ACCENT_CLASSES[color] ?? BADGE_ACCENT_CLASSES.purple
}
